import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSecurityAudit } from '@/hooks/useSecurityAudit';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { toError } from '@/utils/toError';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'doctor' | 'nurse' | 'resident' | 'user';
  fallbackPath?: string;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  requiredRole,
  fallbackPath = '/auth' 
}) => {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logSecurityEvent } = useSecurityAudit();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [roleLoading, setRoleLoading] = useState(true);

  useEffect(() => {
    // Skip role fetch if no specific role is required for this route
    if (!requiredRole) {
      setUserRole('user');
      setRoleLoading(false);
      return;
    }

    const getUserRole = async () => {
      if (!user) {
        setRoleLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('healthcare_providers')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          logger.error('Error fetching user role:', error.message);
          setUserRole('user');
        } else {
          setUserRole(data?.role || 'user');
        }
      } catch (error) {
        logger.error('Error fetching user role:', toError(error).message);
        setUserRole('user');
      } finally {
        setRoleLoading(false);
      }
    };

    getUserRole();
  }, [user, requiredRole]);

  useEffect(() => {
    if (!isLoading && !roleLoading) {
      if (!user) {
        // Log tentative d'accès non authentifié
        logSecurityEvent({
          action: 'unauthorized_access_attempt',
          resource: location.pathname,
        });
        navigate(fallbackPath, { replace: true });
      } else if (requiredRole && userRole) {
        // Vérification du rôle basée sur la hiérarchie
        const roleHierarchy = {
          'user': 1,
          'resident': 2,
          'nurse': 3,
          'doctor': 4,
          'admin': 5
        };

        const userRoleLevel = roleHierarchy[userRole as keyof typeof roleHierarchy] || 1;
        const requiredRoleLevel = roleHierarchy[requiredRole];

        if (userRoleLevel < requiredRoleLevel) {
          logSecurityEvent({
            action: 'insufficient_permissions',
            resource: location.pathname,
          });
          navigate('/', { replace: true });
          return;
        }
      }
    }
  }, [user, isLoading, roleLoading, userRole, navigate, fallbackPath, requiredRole, location.pathname, logSecurityEvent]);

  if (isLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t('common.checkingPermissions')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // La redirection sera gérée par useEffect
  }

  return <>{children}</>;
};

export default AuthGuard;